import { Log } from 'services/log';

// types
import type { Store } from 'redux';
import type { AppRouterInstance } from 'next/dist/shared/lib/app-router-context.shared-runtime';

import {
  TPP_TYPE_DOWNLOAD,
  TPP_TYPE_DOWNLOADCENTER,
  TPP_TYPE_EVENTS,
  TPP_TYPE_IMAGE,
  TPP_TYPE_META_NAVIGATION,
  TPP_TYPE_NEWSECTION,
  TPP_TYPE_SHORTURL,
  TPP_TYPE_SLIDE,
  TPP_TYPE_TECHNICAL,
  TPP_TYPE_TILES,
  TPP_TYPE_TRANSLATION,
  TPP_TYPE_TRANSLATION_STATICPAGE,
  TPP_TYPE_VIDEOCHANNEL,
} from './tppConstants';
import { locatorConfigurationSelector } from 'utils/selectors/globalsSelectors';
import { currentPageSelector } from 'utils/selectors/pageSelectors';

function handleBookmark(button) {
  const isVisible = ({ $node }) =>
    ![
      TPP_TYPE_IMAGE,
      TPP_TYPE_SLIDE,
      TPP_TYPE_TECHNICAL,
      TPP_TYPE_SHORTURL,
      TPP_TYPE_NEWSECTION,
      TPP_TYPE_TRANSLATION,
      TPP_TYPE_DOWNLOADCENTER,
      TPP_TYPE_TILES,
      TPP_TYPE_EVENTS,
      TPP_TYPE_META_NAVIGATION,
      TPP_TYPE_TRANSLATION_STATICPAGE,
    ].includes($node.dataset.editType);
  button.isVisible = isVisible;
  button.isEnabled = isVisible;
}

function handleWorkflows(button, router) {
  button.isVisible = ({ $node, status }) => {
    if (
      status.parts?.[0] === 'translationKey' ||
      (status.parts?.[0] === 'newShortUrl' && status.parts?.[1] === 'ContentAreaPlaceholder') ||
      status.elementType === 'Section'
    ) {
      return false;
    }

    if (
      $node?.dataset?.editType &&
      ![
        TPP_TYPE_IMAGE,
        TPP_TYPE_TECHNICAL,
        TPP_TYPE_VIDEOCHANNEL,
        TPP_TYPE_SHORTURL,
        TPP_TYPE_TRANSLATION_STATICPAGE,
        TPP_TYPE_DOWNLOAD,
      ].includes($node.dataset.editType)
    ) {
      return false;
    }

    return true;
  };

  if (router.asPath === '/_downloads/') {
    // according to the docs the tpp button offers a  afterExecution callback,
    // but this callback is never triggered. so here is a litte workaround to
    // notify the downloads page, that the workflow of the items may need to
    // be updated
    const originalExecute = button.execute;
    button.execute = (scope, item) => {
      try {
        originalExecute(scope, item);
      } catch (e) {
        Log.warn('TPP execution error', e);
      } finally {
        setTimeout(() => {
          window.dispatchEvent(
            new CustomEvent('fscc.update.downloads', {
              detail: {
                type: 'workflow',
              },
            }),
          );
        });
      }
    };
  }
}

function handleTranslate(button) {
  button.isVisible = ({ status }) =>
    !(
      status?.parts?.[0] === 'translationKey' ||
      status.elementType === 'PageRef' ||
      status.elementType === 'SectionReference' ||
      status.elementType === 'Media' ||
      status.elementType === 'Dataset[events]' ||
      status.elementType === 'Dataset[short_urls]'
    );
  button.isEnabled = ({ status }) => {
    return status?.permissions?.change;
  };
}

function handleEdit(button, store: Store<AppState>, pathname: string) {
  button.isVisible = ({ $node, status }) =>
    status?.elementType !== 'SectionReference' && $node.dataset.editType !== TPP_TYPE_IMAGE;

  button.isEnabled = ({ status }) => {
    const page = currentPageSelector(store.getState());

    // edit button is only enabled for the locator overview page
    if (page?.type === 'showroom_locator' && status?.elementType === 'PageRef') {
      const locatorConfig = locatorConfigurationSelector(store.getState());
      const locatorPath = locatorConfig?.url;

      return pathname === locatorPath && !window.location.search.includes('lat');
    }

    return status?.permissions?.change;
  };
}

function handleReferenceUpdate(button) {
  button.isVisible = ({ previewId, status }) =>
    /^#\d+/.test(previewId) &&
    // previewId.startsWith('#') && // standard logic of TPP; left here in case we need to replace it
    // !isNaN(parseInt(previewId.substring(1))) &&
    status.elementType !== 'SectionReference';
  button.isEnabled = ({ status }) => status?.permissions?.change;
}

function handleReferenceCreate(button) {
  button.isVisible = ({ previewId, status }) =>
    /^#\D+/.test(previewId) &&
    // previewId.startsWith('#') && // standard logic of TPP; left here in case we need to replace it
    // isNaN(parseInt(previewId.substring(1))) &&
    status.elementType !== 'SectionReference';
  button.isEnabled = ({ status }) => status?.permissions?.change;
}

function handleInEdit(button) {
  button.isVisible = ({ $node, status }) =>
    !$node.isContentEditable && !!$node.dataset.type && status.elementType !== 'SectionReference';
  button.isEnabled = ({ status }) => status?.permissions?.change;
}

function checkButton(button, router, store: Store<AppState>, pathname: string) {
  switch (button._name) {
    case 'bookmark':
      handleBookmark(button);
      break;

    case 'workflows':
      handleWorkflows(button, router);
      break;

    case 'translate':
      handleTranslate(button);
      break;

    case 'edit':
      handleEdit(button, store, pathname);
      break;

    case 'edit-component': // remove child editing of referenced sections
    case 'delete-component': // remove child removal of referenced sections
    case 'nested-component-move': // remove child removal of referenced sections
      handleReferenceUpdate(button);
      break;

    case 'create-component': // remove adding child of referenced sections
      handleReferenceCreate(button);
      break;

    // disable inEdit for referenced sections and children
    case 'inedit-start-editing':
      handleInEdit(button);
      break;
  }
}

export function defineButtons(router: AppRouterInstance, store: Store<AppState>, pathname: string) {
  window.TPP_SNAP._buttons.forEach((button) => checkButton(button, router, store, pathname));
}
